import type {
  RdpFailure,
  Server,
  TerminalAuth,
  TerminalFailure,
  TerminalAccessLevel,
  TerminalPairOffReason,
} from "@cloudserver/contracts";
import { useSyncExternalStore } from "react";

/*
 * The open sessions of this page — SSH terminals, remote desktops and server
 * consoles: tabs in the top bar, panes in the workspace. A plain store
 * outside React, so sessions keep running while the person moves through
 * the panel; the xterm instances, desktops and VNC clients live next to it
 * (`connections.ts`, `rdp/connection.ts`, `pages/server/console/connection.ts`).
 * Who the tabs are survives a reload of the page (`persist.ts`,
 * `restore.ts`); nothing outlives the browser tab, except the order of the
 * tabs, which is remembered per browser. How terminals look (font, size,
 * theme) is in `settings.ts`.
 */

export type TabStatus = "connecting" | "connected" | "disconnected";
export type Layout = "single" | "columns" | "rows";
/**
 * An SSH terminal, a Windows remote desktop (`rdp/`, docs/rdp.md) or the
 * server's console over VNC (`pages/server/console/`, docs/console.md).
 */
export type SessionKind = "ssh" | "rdp" | "vnc";
/** Why a console is not connected: it ended cleanly, or the connection broke. */
export type VncFailure = "closed" | "failed";

export interface TerminalTab {
  /** Local id of the tab (a reconnect keeps it, the server session id changes). */
  id: string;
  /** Absent: an SSH terminal. */
  kind?: SessionKind;
  projectId: string;
  serverId: string;
  serverName: string;
  osFlavor: string | null;
  osVersion: string | null;
  /** The Unix user; the Windows user of a remote desktop; empty for a console. */
  unixUser: string;
  auth: TerminalAuth;
  status: TabStatus;
  /** Why it is not connected, when it failed or ended (per kind). */
  failure: TerminalFailure | RdpFailure | VncFailure | null;
  /** After `host_key_changed`: the fingerprint the server offers now. */
  offeredHostKey: string | null;
  /** The API refused to open a session: its reason (`server_not_running`, …), or `unexpected`. */
  problem: string | null;
  hostKeyFingerprint: string | null;
  /** Our session id (the certificate's key id) of the current connection. */
  sessionId: string | null;
  /**
   * A terminal's one-time token to come back to the same shell after a
   * reload (the last `ready` frame's; docs/terminal.md, "After a reload").
   */
  resumeToken?: string | null;
  /** Output arrived while the tab was not on screen. */
  unseen: boolean;
  /**
   * Pair mode (SSH only, docs/terminal.md "Working together with the
   * assistant"): whether the assistant may type here now, the command it runs,
   * and why it went off. Absent: never switched on in this connection.
   */
  pair?: TabPair | null;
}

/** The assistant's pair mode in one terminal session, as the API's frames say. */
export interface TabPair {
  on: boolean;
  /** The command the assistant runs now; null while none runs. */
  command: string | null;
  reason: TerminalPairOffReason | null;
  /** The Autopilot's access level here (docs/assistant.md, "Terminal access"); `off` when off. */
  level: TerminalAccessLevel;
}

/** What the "Connect to server" picker starts with: a project, or a server of it. */
export interface PickerRequest {
  projectId?: string;
  server?: Server;
}

export interface TerminalState {
  /** In the order of the top bar (the person may reorder them). */
  tabs: TerminalTab[];
  /** The focused tab: the toolbar acts on it, typing goes to it. Always one of `panes`. */
  active: string | null;
  layout: Layout;
  /**
   * The tabs of the split view in screen order (left or top first), two when
   * there are two tabs. Places are fixed: focusing a pane never moves it.
   * The single view shows only the active tab and keeps these for later.
   */
  panes: string[];
  /**
   * The workspace page is open (tabs on screen do not collect unseen output).
   * Not visible with tabs open: the sessions are minimised into their tabs in
   * the top bar («Свернуть во вкладку», or any other page), still connected.
   */
  visible: boolean;
  picker: PickerRequest | null;
}

const USER_KEY = (serverId: string) => `cs.terminal.user.${serverId}`;

function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function store(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private windows may refuse storage; the default comes back next time.
  }
}

/** The Unix user last used on the server from this browser (`root` by default). */
export const rememberedUser = (serverId: string): string => stored(USER_KEY(serverId)) ?? "root";
export const rememberUser = (serverId: string, user: string): void =>
  store(USER_KEY(serverId), user);

const RDP_USER_KEY = (serverId: string) => `cs.rdp.user.${serverId}`;
/** The Windows user last used on the server's remote desktop (`Administrator` by default). */
export const rememberedRdpUser = (serverId: string): string =>
  stored(RDP_USER_KEY(serverId)) ?? "Administrator";
export const rememberRdpUser = (serverId: string, user: string): void =>
  store(RDP_USER_KEY(serverId), user);

/** The kind of a tab (tabs made before the kind existed are terminals). */
export const kindOf = (tab: Pick<TerminalTab, "kind">): SessionKind => tab.kind ?? "ssh";

/**
 * Only terminals share the workspace in a split view; a remote desktop or a
 * console takes the whole workspace (docs/terminal.md, "Workspace").
 */
const inPanes = (tab: Pick<TerminalTab, "kind"> | undefined) => !!tab && kindOf(tab) === "ssh";

/** What a tab is called on buttons and in lists: `root@web-1`, or `web-1` for a console. */
export const sessionTitle = (tab: Pick<TerminalTab, "kind" | "unixUser" | "serverName">) =>
  kindOf(tab) === "vnc" || !tab.unixUser ? tab.serverName : `${tab.unixUser}@${tab.serverName}`;

/**
 * A tab rebuilt after a reload that cannot reconnect by itself: a remote
 * desktop or a terminal signed in with a password (passwords are never
 * kept). Its card asks for the password (`problem` of the tab).
 */
export const RESTORED = "restored";

/**
 * The API no longer knows the tab's server (deleted, or the person lost the
 * project): the tab says «Сервер не найден» and only its ✕ is left.
 */
export const serverGone = (tab: Pick<TerminalTab, "problem">): boolean =>
  tab.problem === "not_found" || tab.problem === "project_not_found";

/**
 * What the status dot and its words say, the same for every kind: a session
 * the person or the server ended normally (closed, idle, too long), or one
 * waiting for its password after a reload, is `disconnected` (grey);
 * anything that went wrong is an `error` (red).
 */
export type SessionState = "connecting" | "connected" | "disconnected" | "error";
const CLEAN_ENDS: readonly string[] = ["closed", "idle", "lifetime"];

export function sessionState(
  tab: Pick<TerminalTab, "status" | "failure" | "problem">,
): SessionState {
  if (tab.status !== "disconnected") return tab.status;
  if (tab.problem === RESTORED) return "disconnected";
  return !tab.problem && tab.failure && CLEAN_ENDS.includes(tab.failure) ? "disconnected" : "error";
}

/** Why the terminal cannot open on this server now, or null. */
export function serverBlock(
  server: Pick<Server, "status" | "publicIpv4" | "publicIpv6">,
): "off" | "noAddress" | null {
  if (server.status !== "running") return "off";
  if (!server.publicIpv4 && !server.publicIpv6) return "noAddress";
  return null;
}

// ---- Tab order --------------------------------------------------------------

const ORDER_KEY = "cs.sessions.order";
/** Enough for every session a person keeps around; the oldest entries fall off. */
const ORDER_LIMIT = 100;

/** Who a tab is across visits — kind, server and user: the order is remembered by it. */
export const orderKey = (tab: Pick<TerminalTab, "kind" | "serverId" | "unixUser">): string =>
  kindOf(tab) === "vnc" ? `vnc:${tab.serverId}` : `${kindOf(tab)}:${tab.serverId}:${tab.unixUser}`;

/**
 * The remembered order after the person reordered the open tabs: the open
 * tabs' keys take the places they held, in their new order; keys of tabs
 * not open now keep theirs; keys new to this browser go to the end.
 */
export function mergeOrder(remembered: readonly string[], current: readonly string[]): string[] {
  const open = new Set(current);
  const queue = current.filter((key) => remembered.includes(key));
  const merged = remembered.map((key) => (open.has(key) ? (queue.shift() ?? key) : key));
  const known = new Set(merged);
  return [...merged, ...current.filter((key) => !known.has(key))].slice(-ORDER_LIMIT);
}

/**
 * Where a new tab goes among the open ones: before the first open tab that
 * the remembered order puts after it; at the end when it is new here.
 */
export function insertIndex(
  openKeys: readonly string[],
  key: string,
  remembered: readonly string[],
): number {
  const rank = remembered.indexOf(key);
  if (rank < 0) return openKeys.length;
  const after = openKeys.findIndex((k) => remembered.indexOf(k) > rank);
  return after < 0 ? openKeys.length : after;
}

/** Where the order is kept. */
export interface OrderStorage {
  read(): string[];
  write(order: string[]): void;
}

/** `localStorage` (`cs.sessions.order`); a refused or damaged entry reads as no order. */
export const browserOrder: OrderStorage = {
  read() {
    try {
      const parsed: unknown = JSON.parse(stored(ORDER_KEY) ?? "[]");
      return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === "string") : [];
    } catch {
      return [];
    }
  },
  write(order) {
    store(ORDER_KEY, JSON.stringify(order));
  },
};

// ---- Putting a command into an open session ---------------------------------

/** Kinds that take a shell command, best first: a terminal pastes, the others type. */
const INSERT_RANK: Record<SessionKind, number> = { ssh: 0, vnc: 1, rdp: 2 };

/**
 * The open, connected sessions of a server a command can be put into (the
 * assistant's «Вставить в терминал», a command a page hands to the console):
 * the focused tab first, then terminals, consoles and desktops, in tab order
 * within a kind. Empty: a new session has to be opened.
 */
export function insertTargets(
  state: Pick<TerminalState, "tabs" | "active">,
  serverId: string,
): TerminalTab[] {
  return state.tabs
    .map((tab, index) => ({ tab, index }))
    .filter(({ tab }) => tab.serverId === serverId && tab.status === "connected")
    .sort(
      (a, b) =>
        Number(b.tab.id === state.active) - Number(a.tab.id === state.active) ||
        INSERT_RANK[kindOf(a.tab)] - INSERT_RANK[kindOf(b.tab)] ||
        a.index - b.index,
    )
    .map(({ tab }) => tab);
}

/** The console tab of a server: one per server, since two would show the same screen. */
export function consoleTabOf(
  state: Pick<TerminalState, "tabs">,
  serverId: string,
): TerminalTab | undefined {
  return state.tabs.find((t) => kindOf(t) === "vnc" && t.serverId === serverId);
}

// ---- Workspace ------------------------------------------------------------------

/** The tabs the workspace draws, in screen order (whether or not it is open). */
export function paneTabs(
  state: Pick<TerminalState, "active" | "layout" | "panes" | "tabs">,
): string[] {
  if (!state.active) return [];
  // A remote desktop or a console takes the whole workspace: split views are for terminals.
  if (state.layout === "single" || !inPanes(state.tabs.find((t) => t.id === state.active)))
    return [state.active];
  return state.panes.includes(state.active) ? state.panes : [state.active];
}

/** The tabs on screen right now: none while the workspace is closed. */
export function shownTabs(state: TerminalState): string[] {
  return state.visible ? paneTabs(state) : [];
}

/**
 * How a tab in the top bar looks (docs/terminal.md, "Session window"):
 * `open` — the focused tab of the workspace on screen; `minimised` — the
 * focused tab while the workspace is hidden, the one «Свернуть во вкладку» shrank the
 * window into and a click grows it back from; `idle` — any other tab.
 */
export type TabLook = "open" | "minimised" | "idle";

export function tabLook(state: Pick<TerminalState, "visible" | "active">, id: string): TabLook {
  if (state.active !== id) return "idle";
  return state.visible ? "open" : "minimised";
}

/**
 * Up to two different, existing terminal tabs with `active` among them (a
 * remote desktop or a console is never in a pane); filled up from `tabs`.
 */
function fill(panes: readonly string[], active: string | null, all: readonly TerminalTab[]) {
  const tabs = all.filter((t) => inPanes(t));
  const known = panes.filter((p, i) => panes.indexOf(p) === i && tabs.some((t) => t.id === p));
  if (active && !known.includes(active) && tabs.some((t) => t.id === active)) {
    if (known.length >= 2) known[1] = active;
    else known.push(active);
  }
  for (const tab of tabs) {
    if (known.length >= 2) break;
    if (!known.includes(tab.id)) known.push(tab.id);
  }
  return known.slice(0, 2);
}

export class TerminalStore {
  private state: TerminalState;
  private readonly listeners = new Set<() => void>();

  constructor(
    initial: Partial<TerminalState> = {},
    private readonly order: OrderStorage = browserOrder,
  ) {
    this.state = {
      tabs: [],
      active: null,
      layout: "single",
      panes: [],
      visible: false,
      picker: null,
      ...initial,
    };
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly snapshot = (): TerminalState => this.state;

  private set(next: Partial<TerminalState>): void {
    this.state = { ...this.state, ...next };
    for (const listener of this.listeners) listener();
  }

  tab(id: string): TerminalTab | undefined {
    return this.state.tabs.find((t) => t.id === id);
  }

  /**
   * A new session opens next to the focused one (in the other pane) and gets
   * the focus; its tab goes where the remembered order puts it.
   */
  add(tab: TerminalTab): void {
    const { active, panes } = this.state;
    const other = panes.find((p) => p !== active);
    const next = !inPanes(tab)
      ? panes
      : panes.length < 2
        ? [...panes, tab.id]
        : panes.map((p) => (p === other ? tab.id : p));
    const tabs = [...this.state.tabs];
    tabs.splice(insertIndex(tabs.map(orderKey), orderKey(tab), this.order.read()), 0, tab);
    this.set({ tabs, active: tab.id, panes: next });
  }

  /**
   * The tabs of this browser tab after a reload, in their old order, with
   * the focus and the split view they had (`restore.ts`); tabs opened
   * meanwhile stay after them.
   */
  restore(
    restored: readonly TerminalTab[],
    view: { active: string | null; layout: Layout; panes: readonly string[] },
  ): void {
    if (restored.length === 0) return;
    const ids = new Set(restored.map((tab) => tab.id));
    const tabs = [...restored, ...this.state.tabs.filter((tab) => !ids.has(tab.id))];
    const active =
      this.state.active ??
      (view.active && tabs.some((tab) => tab.id === view.active) ? view.active : tabs[0]?.id) ??
      null;
    this.set({ tabs, active, layout: view.layout, panes: fill(view.panes, active, tabs) });
  }

  /** Moves a tab to `index` of the bar (dragging) and remembers the order for this browser. */
  moveTo(id: string, index: number): void {
    const from = this.state.tabs.findIndex((t) => t.id === id);
    const to = Math.max(0, Math.min(this.state.tabs.length - 1, index));
    if (from < 0 || from === to) return;
    const tabs = [...this.state.tabs];
    const [moved] = tabs.splice(from, 1);
    if (moved) tabs.splice(to, 0, moved);
    this.set({ tabs });
    this.order.write(mergeOrder(this.order.read(), tabs.map(orderKey)));
  }

  /** One place to the left (-1) or right (+1): the keyboard shortcut and the tab's menu. */
  move(id: string, delta: -1 | 1): void {
    const from = this.state.tabs.findIndex((t) => t.id === id);
    if (from >= 0) this.moveTo(id, from + delta);
  }

  update(id: string, patch: Partial<TerminalTab>): void {
    if (!this.tab(id)) return;
    this.set({ tabs: this.state.tabs.map((t) => (t.id === id ? { ...t, ...patch } : t)) });
  }

  /** New output: marks the tab when it is not on screen. */
  output(id: string): void {
    const tab = this.tab(id);
    if (!tab || tab.unseen || shownTabs(this.state).includes(id)) return;
    this.update(id, { unseen: true });
  }

  remove(id: string): void {
    const index = this.state.tabs.findIndex((t) => t.id === id);
    if (index < 0) return;
    const tabs = this.state.tabs.filter((t) => t.id !== id);
    const { panes } = this.state;
    // The closed tab's place shows a tab that is in no pane yet.
    const spare = tabs.find((t) => inPanes(t) && !panes.includes(t.id))?.id;
    const kept = panes.flatMap((p) => (p !== id ? [p] : spare ? [spare] : []));
    let active = this.state.active;
    if (active === id) {
      // The other pane takes the focus, else the neighbouring tab.
      active = panes.find((p) => p !== id) ?? tabs[Math.min(index, tabs.length - 1)]?.id ?? null;
    }
    this.set({ tabs, active, panes: fill(kept, active, tabs) });
  }

  /**
   * Puts a tab in front. A tab already in a pane only gets the focus; any
   * other one is shown in the focused pane. Panes never trade places.
   */
  activate(id: string): void {
    if (!this.tab(id)) return;
    const { active, panes } = this.state;
    const next =
      panes.includes(id) || !inPanes(this.tab(id))
        ? panes
        : fill(
            panes.map((p) => (p === active ? id : p)),
            id,
            this.state.tabs,
          );
    this.set({
      active: id,
      panes: next,
      tabs: this.state.tabs.map((t) => (t.id === id ? { ...t, unseen: false } : t)),
    });
  }

  setLayout(layout: Layout): void {
    this.set({ layout, panes: fill(this.state.panes, this.state.active, this.state.tabs) });
    this.clearShown();
  }

  /** The other pane of the split view shows this tab; the focused pane stays as it is. */
  setOther(id: string): void {
    const { active, panes } = this.state;
    if (!inPanes(this.tab(id)) || id === active || panes.includes(id)) return;
    const other = panes.find((p) => p !== active);
    this.set({ panes: other ? panes.map((p) => (p === other ? id : p)) : [...panes, id] });
    this.clearShown();
  }

  /**
   * A terminal tab dropped onto a pane of the split view: that pane shows it
   * and gets the focus; the other pane stays. A tab already in a pane only
   * gets the focus; a desktop or a console is never put in a pane.
   */
  placeIn(id: string, pane: string): void {
    const { panes } = this.state;
    if (!inPanes(this.tab(id)) || !panes.includes(pane)) return;
    if (panes.includes(id)) {
      this.activate(id);
      return;
    }
    this.set({ panes: panes.map((p) => (p === pane ? id : p)) });
    this.activate(id);
    this.clearShown();
  }

  setVisible(visible: boolean): void {
    if (this.state.visible === visible) return;
    this.set({ visible });
    this.clearShown();
  }

  openPicker(request: PickerRequest = {}): void {
    this.set({ picker: request });
  }

  closePicker(): void {
    this.set({ picker: null });
  }

  private clearShown(): void {
    const shown = shownTabs(this.state);
    if (!this.state.tabs.some((t) => t.unseen && shown.includes(t.id))) return;
    this.set({
      tabs: this.state.tabs.map((t) => (shown.includes(t.id) ? { ...t, unseen: false } : t)),
    });
  }
}

/** The page's one store. */
export const terminals = new TerminalStore();

export function useTerminals(): TerminalState {
  return useSyncExternalStore(terminals.subscribe, terminals.snapshot, terminals.snapshot);
}
